import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import {
  CalendarToday,
  Add,
  Event,
  Person,
  LocalHospital,
  Schedule,
  Edit,
  Delete,
  Notifications,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useDataContext } from '../contexts/DataContext';
import { useFeedback } from './EnhancedFeedbackProvider';

const MedicalCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const { showSuccess, showError } = useFeedback();
  const { consultations: appointments, patients, addConsultation, updateConsultation, deleteConsultation } = useDataContext();

  const [formData, setFormData] = useState({
    patientId: '',
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    type: 'consultation',
    status: 'scheduled'
  });


  const handleDateClick = (date) => {
    setSelectedDate(date);
    setDialogOpen(true);
    setEditingAppointment(null);
    setFormData({
      patientId: '',
      title: '',
      description: '',
      start_time: format(date, 'yyyy-MM-dd\'T\'HH:mm'),
      end_time: format(new Date(date.getTime() + 60 * 60 * 1000), 'yyyy-MM-dd\'T\'HH:mm'),
      type: 'consultation',
      status: 'scheduled'
    });
  };

  const handleAppointmentClick = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      patientId: appointment.patientId,
      title: appointment.title || appointment.reason,
      description: appointment.description || appointment.notes,
      start_time: appointment.start_time || appointment.consultation_date,
      end_time: appointment.end_time || appointment.next_appointment,
      type: appointment.type || 'consultation',
      status: appointment.status || 'scheduled'
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const consultationData = {
        ...formData,
        consultation_date: formData.start_time,
        next_appointment: formData.end_time,
        reason: formData.title,
        notes: formData.description
      };

      if (editingAppointment) {
        await updateConsultation(editingAppointment.id, consultationData);
        showSuccess('Rendez-vous modifié avec succès');
      } else {
        await addConsultation(consultationData);
        showSuccess('Rendez-vous créé avec succès');
      }
      
      setDialogOpen(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showError('Erreur lors de la sauvegarde du rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (appointment) => {
    try {
      await deleteConsultation(appointment.id);
      showSuccess('Rendez-vous supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showError('Erreur lors de la suppression du rendez-vous');
    }
  };

  const getAppointmentsForDate = (date) => {
    return appointments.filter(appointment => 
      isSameDay(new Date(appointment.consultation_date || appointment.start_time), date)
    );
  };

  const getAppointmentTypeColor = (type) => {
    switch (type) {
      case 'consultation':
        return 'primary';
      case 'urgent':
        return 'error';
      case 'follow_up':
        return 'warning';
      case 'surgery':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getAppointmentTypeIcon = (type) => {
    switch (type) {
      case 'consultation':
        return <Event />;
      case 'urgent':
        return <Warning />;
      case 'follow_up':
        return <Schedule />;
      case 'surgery':
        return <LocalHospital />;
      default:
        return <CalendarToday />;
    }
  };

  const renderCalendarGrid = () => {
    const startDate = startOfMonth(currentDate);
    const endDate = endOfMonth(currentDate);
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    
    // Ajouter les jours du mois précédent pour compléter la première semaine
    const firstDay = startDate.getDay();
    const previousMonthDays = [];
    for (let i = firstDay - 1; i >= 0; i--) {
      const date = new Date(startDate);
      date.setDate(date.getDate() - i - 1);
      previousMonthDays.push(date);
    }

    const allDays = [...previousMonthDays, ...days];

    return (
      <Grid container spacing={1}>
        {/* En-têtes des jours */}
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
          <Grid item xs key={day}>
            <Typography variant="subtitle2" align="center" fontWeight={600} color="text.secondary">
              {day}
            </Typography>
          </Grid>
        ))}
        
        {/* Jours du calendrier */}
        {allDays.map((date, index) => {
          const dayAppointments = getAppointmentsForDate(date);
          const isCurrentMonth = date.getMonth() === currentDate.getMonth();
          const isCurrentDay = isToday(date);
          
          return (
            <Grid item xs key={index}>
              <Box
                sx={{
                  minHeight: 100,
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: isCurrentDay 
                    ? alpha(theme.palette.primary.main, 0.1)
                    : isCurrentMonth 
                      ? 'background.paper' 
                      : 'grey.50',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                  position: 'relative'
                }}
                onClick={() => handleDateClick(date)}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isCurrentDay ? 600 : 400,
                    color: isCurrentMonth ? 'text.primary' : 'text.disabled',
                    mb: 0.5
                  }}
                >
                  {format(date, 'd')}
                </Typography>
                
                {/* Rendez-vous */}
                <Box>
                  {dayAppointments.slice(0, 2).map((appointment, appointmentIndex) => (
                    <Chip
                      key={appointmentIndex}
                      label={appointment.title || appointment.reason || 'Rendez-vous'}
                      size="small"
                      color={getAppointmentTypeColor(appointment.type)}
                      icon={getAppointmentTypeIcon(appointment.type)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAppointmentClick(appointment);
                      }}
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        mb: 0.5,
                        '& .MuiChip-icon': {
                          fontSize: '0.8rem'
                        }
                      }}
                    />
                  ))}
                  
                  {dayAppointments.length > 2 && (
                    <Typography variant="caption" color="text.secondary">
                      +{dayAppointments.length - 2} autres
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderAppointmentList = () => {
    const todayAppointments = getAppointmentsForDate(selectedDate || new Date());
    
    return (
      <List>
        {todayAppointments.map((appointment) => {
          const patient = patients.find(p => p.id === appointment.patientId);
          
          return (
            <ListItem
              key={appointment.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: 1,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon>
                <Avatar sx={{ bgcolor: `${getAppointmentTypeColor(appointment.type)}.main` }}>
                  {getAppointmentTypeIcon(appointment.type)}
                </Avatar>
              </ListItemIcon>
              
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {appointment.title || appointment.reason || 'Rendez-vous'}
                    </Typography>
                    <Chip
                      label={appointment.status || 'Programmé'}
                      size="small"
                      color={appointment.status === 'completed' ? 'success' : 'default'}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {patient ? `${patient.firstName} ${patient.lastName}` : 'Patient inconnu'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(appointment.consultation_date), 'HH:mm')} - 
                      {format(new Date(appointment.next_appointment || appointment.consultation_date), 'HH:mm')}
                    </Typography>
                  </Box>
                }
              />
              
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  onClick={() => handleAppointmentClick(appointment)}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(appointment)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <Box>
      {/* En-tête du calendrier */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Calendrier Médical
        </Typography>
        
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
          >
            Précédent
          </Button>
          
          <Typography variant="h6" fontWeight={600}>
            {format(currentDate, 'MMMM yyyy', { locale: fr })}
          </Typography>
          
          <Button
            variant="outlined"
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
          >
            Suivant
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Calendrier */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {renderCalendarGrid()}
            </CardContent>
          </Card>
        </Grid>

        {/* Liste des rendez-vous du jour */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Rendez-vous du {selectedDate ? format(selectedDate, 'dd MMMM yyyy', { locale: fr }) : format(new Date(), 'dd MMMM yyyy', { locale: fr })}
              </Typography>
              
              {renderAppointmentList()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog pour créer/modifier un rendez-vous */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAppointment ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Patient</InputLabel>
                <Select
                  value={formData.patientId}
                  onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
                  label="Patient"
                >
                  {patients.map((patient) => (
                    <MenuItem key={patient.id} value={patient.id}>
                      {patient.firstName} {patient.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  label="Type"
                >
                  <MenuItem value="consultation">Consultation</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="follow_up">Suivi</MenuItem>
                  <MenuItem value="surgery">Chirurgie</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titre"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Heure de début"
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Heure de fin"
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} variant="contained">
            {editingAppointment ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalCalendar;
